import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  KeyboardEvent,
  MutableRefObject,
} from "react";
import { Input, Icon, InputProps } from "semantic-ui-react";
import CardMessageList from "./CardMessageList";

const COLORS = {
  dark_grey: "#746A6A",
  light_grey: "#D9D9D9",
};

type SearchBoxProps = {
  // data
  total?: number;
  activeIndex?: number;
  searchList?: any[];
  laxyLoad?: () => "next" | "prev";
  // callback
  onChangeIndex?: (index: number) => void;
  onClose?: () => void;
  onSearch?: (value: string) => void;
  onNextSearch?: (value: string) => void;
  onSelectMessage?: (item: any, index: number) => void;
};

const SearchBox = React.memo<SearchBoxProps>((props) => {
  const [input, setInput] = useState<string>("");
  const [toggleMessageList, setToggleMessageList] = useState<boolean>(false);
  const [isNotFound, setIsNotFound] = useState(false);

  const searchBoxRef = useRef() as MutableRefObject<HTMLDivElement>;
  const inputRef = useRef<any>();
  const isSearch = useRef<boolean>(false);

  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.inputRef.current.focus();
      }, 100);
    }
  }, [inputRef.current]);

  useEffect(() => {
    if (!props.searchList?.length && isSearch.current && input) {
      setIsNotFound(true);
    } else {
      setIsNotFound(false);
    }
  }, [props.searchList]);

  const messageSearchStyle = useMemo(() => {
    if (searchBoxRef.current) {
      const body = document.querySelector("body") as HTMLBodyElement;
      const { bottom, height } = searchBoxRef.current.getBoundingClientRect();
      const { bottom: bottomBody } = body.getBoundingClientRect();
      return { top: `${height}px`, height: `${bottomBody - bottom}px` };
    }
    return {};
  }, [window.innerHeight, searchBoxRef.current]);

  // Search
  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    const value = (e.target as any).value;
    if (e.key === "Enter") {
      isSearch.current = true;
      props.onSearch?.((e.target as any).value);
      setInput(value);
    }
  };

  const handleChange = (e: any, v: InputProps) => {
    isSearch.current = false;
    setInput(v.value);
  };

  const handleNextSearch = () => {
    props.onNextSearch?.(input);
  };

  const handleShowMessageList = () => {
    setToggleMessageList(!toggleMessageList);
  };

  const handleChangeStep = (action: string) => (e: any) => {
    console.log(props.laxyLoad?.());

    if (props.laxyLoad?.()) {
      return;
    }

    let index = props.activeIndex || 0;
    const endIndex = (props.total || 0) - 1;
    if (action === "next") {
      index -= 1;
    } else if (action === "previous") {
      index += 1;
    }

    if (index >= (props.searchList?.length || 0)) {
      handleNextSearch();
    }

    props.onChangeIndex?.(index < 0 ? 0 : index > endIndex ? endIndex : index);
  };

  // Message list
  const handleSelected = (item: any, index: number) => {
    props.onSelectMessage?.(item, index);
    setToggleMessageList(false);
  };

  return (
    <div
      ref={searchBoxRef}
      style={{
        display: "flex",
        alignItems: "baseline",
        width: "100%",
        padding: "0 10px 10px",
        position: "relative",
      }}
    >
      <Input
        ref={inputRef}
        icon={
          <div
            style={{
              display: "flex",
              margin: "0.55rem 0 0 -153px",
            }}
          >
            {!!props.total && (
              <label style={{ width: "90px", textAlign: "right" }}>
                {!!props.total && `${(props.activeIndex || 0) + 1}/${props.total || ""}`}
              </label>
            )}
            {isNotFound && (
              <label
                style={{
                  width: "140px",
                  textAlign: "right",
                  color: COLORS.dark_grey,
                }}
              >
                No results
              </label>
            )}

            {!!props.total && (
              <>
                <Icon
                  name="caret up"
                  link
                  style={{
                    fontSize: "2rem",
                    color: COLORS.light_grey,
                    marginRight: "-7.5px",
                  }}
                  onClick={handleChangeStep("previous")}
                />
                <Icon
                  name="caret down"
                  link
                  style={{ fontSize: "2rem", color: COLORS.light_grey }}
                  onClick={handleChangeStep("next")}
                />
              </>
            )}
          </div>
        }
        value={input}
        placeholder="ค้นหาข้อความ"
        fluid
        onKeyPress={handleKeyPress}
        onChange={handleChange}
        style={{ width: "95%", marginRight: "0.75rem" }}
      />
      {!!props.total && (
        <Icon
          name="comments"
          circular={true}
          inverted={toggleMessageList}
          style={{
            fontSize: "1.2rem",
            margin: "0px 1rem 0px 0.05rem",
            color: COLORS.dark_grey,
            cursor: "pointer",
          }}
          onClick={handleShowMessageList}
        />
      )}
      <Icon name="close" color="red" onClick={props.onClose} style={{ cursor: "pointer" }} />

      {/* Display message search */}
      {toggleMessageList && (
        <CardMessageList
          // data
          searchList={props.searchList}
          search={input}
          isEndOfList={!!props.total && props.total === props.searchList?.length}
          style={messageSearchStyle}
          // callback
          onScrollToBottom={handleNextSearch}
          onSelected={handleSelected}
        />
      )}
    </div>
  );
});

SearchBox.displayName = "SearchBox";

export default SearchBox;
